import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { IMainLang } from '@interfaces/languages/main-lang';
import { HttpOptionsService } from '@services/http-options.service';
import { BehaviorSubject, Observable, of, switchMap, throwError } from 'rxjs';
import { map, take } from 'rxjs/operators';
import {
    IAppRequest,
    IAppServiceConfig,
    IAppServiceLanguages,
    InterfaceAppSettings,
    IPublicMenuItem,
} from './app.interface';
import { AppSettings } from './core/libraries/Settings/app-settings';
import { TokenStorage } from './core/token.storage';

/**
 * Конфигурация приложения
 */
@Injectable()
export class AppConfig {
    /** Сервис параметров http-запроса */
    protected httpOptions: HttpOptionsService = inject(HttpOptionsService);
    protected http: HttpClient = inject(HttpClient);
    public token: TokenStorage = inject(TokenStorage);

    /** Настройки системы */
    public appSettings: AppSettings;

    /**
     * Данные пользователя
     */
    public userData: {
        photo?: string;
    };

    /**
     * Объект конфигурации
     * @type {null}
     */
    public config: IAppServiceConfig = null;

    /**
     * Объект переводов по языкам
     * @type {null}
     */
    public languageDictionary: IMainLang = null;

    /**
     * Конфигурация сервиса системы
     */
    public languages: { [key: string]: IAppServiceLanguages } | null = null;

    /**
     * Язык по умолчанию
     */
    public defaultLanguage: string = '';

    protected _isAuth: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    public get isAuth(): Observable<boolean> {
        return this._isAuth.asObservable();
    }

    /**
     * Настройки системы
     */
    public settings: { [key: string]: InterfaceAppSettings | any } = {};

    /**
     * URL АПИ системы
     */
    public url = '/api';

    /**
     * Адрес геокодеа
     */
    public urlGeocode = 'https://geocode-maps.yandex.ru/1.x/';
    /**
     * Время для автосохранения в миллисекундах
     */
    public timeSave: number = 1000;
    /**
     * Ограничение на скрытие поисковой строки в селекте
     */
    public hideSelectOptions: number = 5;
    /**
     * Максимальный размер файла для автоматической загрузки (20 Мб)
     */
    public maxSizeFileAutoUpload: number = 20971520;
    /**
     * Максимальный размер файла для загрузки на сервер
     */
    public maxSizeFileUpload: number = 0;

    /**
     * Публичное меню
     */
    public publicMenu: IPublicMenuItem[];

    /**
     * Установить параметр для проверки Captcha
     */
    public captcha: boolean = false;

    /** Получение параметров приложения */
    public load(): Observable<boolean> {
        return this.http.get<IAppRequest>(this.url + '/settings/front_settings/main', this.httpOptions).pipe(
            take(1),
            switchMap((result: IAppRequest) => {
                if (!result || result.is_error) {
                    return throwError(() => {
                        return new Error(result?.message ?? '');
                    });
                }
                return of(result.data);
            }),
            map((data) => {
                this.processConfigData(data);
                return data.is_auth;
            }),
        );
    }

    /** Загрузка атрибутов в класс из response */
    protected processConfigData(data: any): void {
        if (data.menu && data.menu?.length) {
            this.token.saveMenu(data.menu);
        }

        this.token.saveCurrentLang(data.current_language);

        // Настройки языков
        this.languageDictionary = data.language_dictionary;
        this.languages = data.languages ?? null;
        this.defaultLanguage = data.default_language ?? '';

        this.settings = data.settings;
        this._isAuth.next(data.is_auth === true);
        this.userData = data.user_data;
        this.maxSizeFileUpload = data.upload_max_filesize;
        this.publicMenu = data.public_menu;

        // Избегаем создания нового экземпляра класса. В противном случа данные могут отличаться.
        this.appSettings = Object.assign(this.appSettings || {}, new AppSettings(this.settings));
        this.captcha = data.captcha ?? '';

        if (!data.is_auth) {
            this.token.signOut();
        }
    }
}
